import PropTypes from 'prop-types';
import React, { forwardRef, useMemo } from 'react';

import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField as MUITextField } from '@mui/material';
import classNames from 'classnames';
import Cleave from 'cleave.js/react';

import { useInput, withWidget } from '@ezbobdev/ui-commons';
import { useTranslation } from '@ezbobdev/ui-framework';

import { css, styled } from '../../Utils/style';
import MaterialTooltip from '../MaterialTooltip/MaterialTooltip';

const MUITextFieldStyled = styled(MUITextField)`
  ${({ theme }) => css`
    input:-webkit-autofill {
      &,
      &:hover,
      &:focus,
      &:active {
        -webkit-box-shadow: 0 0 0 30px ${theme.palette.common.white} inset;
      }
    }
  `}
`;

const CleaveInput = forwardRef((props, ref) => {
  const { inputRef, privacyRules, formattingOptions, ...rest } = props;

  const setRef = (element) => {
    inputRef.current = element;
  };

  return (
    <Cleave
      options={formattingOptions}
      data-dd-privacy={privacyRules.input}
      htmlRef={inputRef ? setRef : ref}
      {...rest}
    />
  );
});

const MaterialTextField = forwardRef((props, ref) => {
  const { t, i18n } = useTranslation();

  const {
    toggleRevealPassword,
    revealPassword,
    value,
    placeholder,
    autoFocus,
    fullWidth,
    variant,
    validityState,
    wrapperClassNames,
    formattingOptions,
    inputType,
    inputProps,
    inputMode,
    InputProps,
    configByName,
    isRequired,
    privacyRules,
    maskValueVisibility,
    multiline,
    onBlur,
    onFocus,
    onChange,
    captureCleave,
    id,
    label,
    helperText,
    autoComplete,
    passwordVisibilityLabel,
    readOnly,
    disabled,
    isFocused,
  } = useInput(props, MaterialTextField.displayName);

  const inputElement =
    multiline || maskValueVisibility || Object.hasOwn(InputProps || {}, 'inputComponent') ? undefined : CleaveInput;

  return useMemo(() => {
    return (
      <MUITextFieldStyled
        type={inputType === 'password' && revealPassword ? 'text' : inputType}
        name={configByName}
        autoComplete={autoComplete}
        className={classNames(wrapperClassNames)}
        variant={variant}
        id={id}
        label={label}
        placeholder={placeholder}
        value={value}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        error={validityState.validatingStatus === 'invalid'}
        helperText={validityState.errorMessage || helperText}
        FormHelperTextProps={props.FormHelperTextProps}
        required={isRequired}
        disabled={disabled}
        multiline={multiline}
        InputProps={{
          className: classNames({ 'Mui-disabled': disabled || readOnly }),
          inputComponent: inputElement,
          startAdornment: inputType === 'currency' &&
            (isFocused || value) &&
            i18n.exists(`${configByName}.startAdornment`) && (
              <InputAdornment position="start">{t(`${configByName}.startAdornment`)}</InputAdornment>
            ),
          endAdornment: (
            <>
              {InputProps?.endAdornment?.props.children}

              {(inputType === 'password' || revealPassword) && (
                <InputAdornment position="end">
                  <IconButton aria-label={passwordVisibilityLabel} onClick={toggleRevealPassword} edge="end">
                    {revealPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </InputAdornment>
              )}

              {i18n.exists(`${configByName}.tooltip.content`) && <MaterialTooltip name={configByName} />}
            </>
          ),
          ...InputProps,
          inputProps: {
            [inputElement ? 'inputRef' : 'ref']: ref,
            readOnly: readOnly,
            disabled: disabled,
            'aria-required': isRequired,
            ...inputProps,
            ...InputProps?.inputProps,
            ...(inputElement && { formattingOptions, onInit: captureCleave }),
            ...((inputElement || InputProps?.inputComponent) && { privacyRules }),
            inputMode,
            'data-testid': `${configByName}-input`,
          },
        }}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    );
  }, [
    value,
    placeholder,
    revealPassword,
    InputProps,
    inputProps,
    maskValueVisibility,
    formattingOptions,
    validityState,
    multiline,
    isFocused,
  ]);
});

MaterialTextField.displayName = 'MaterialTextField';

MaterialTextField.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  configBy: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
};

export { MaterialTextField as BaseMaterialTextField };

export default React.memo(withWidget(MaterialTextField, { includeCaseMapProps: ['name'] }));
